import classNames from "classnames/bind";
import styles from "./CanvasRegion.module.css";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import axiosClient from "../../../../services/axiosClient";
import { Toast } from "../../../../components";
import { useTranslation } from "react-i18next";
import { getPolygon, getPolygonFire, getRoiList } from "../../../../utils";
import { alprService } from "../../../../services/api/alprService";

const cx = classNames.bind(styles);

function CanvasRegion({ width, height, cameraCode, codeService, nameService, refreshCamera }) {
    const canvasRef = useRef(null);
    const [polygon, setPolygon] = useState({
        polygon1: [],
        polygon2: [],
        polygon3: []
    });
    const [canvas, setCanvas] = useState(null);
    const [reGetData, setReGetData] = useState(false);
    const [path, setPath] = useState();
    const [quality, setQuality] = useState({ width: 0, height: 0 });
    const [restore, setReStore] = useState(false);
    const [cropRegion, setCropRegion] = useState(0);
    const [defaultRoi, setDefaultRoi] = useState(false);
    const { t } = useTranslation()

    const getConfigRoi = async (X, Y) => {
        try {
            const res = await axiosClient.get(`aiConfigRoiList/get-list-ai-config-roi-list/?camera_code=${cameraCode}&ai_service_code=${codeService}`);
            if (nameService?.toLowerCase() === 'nhận dạng biển số xe') {
                const res2 = await alprService.getCrop({ camera_code: cameraCode })
                if (res2.length > 0 && res2[0].CROP_REGION && JSON.parse(res2[0].CROP_REGION).length > 0) {
                    setCropRegion(JSON.parse(res2[0].CROP_REGION)[0][0] / 100)
                } else {
                    setCropRegion(0)
                }
            }
            let data = {};
            res.forEach(item => {
                data = item;
            })
            if (res && res.length > 0) {
                const arr = getRoiList(data.ROI_LIST);
                setDefaultRoi(true);
                const { polygon1, polygon2, polygon3 } = getPolygon(arr, width, height);
                setPolygon({
                    polygon1: polygon1,
                    polygon2: polygon2,
                    polygon3: polygon3,
                })
            }
        } catch (error) {
            console.log(error);
            
            Toast.open(t('setting.Unable_to_extract_recognition_areas'), 'danger')
        }
        // eslint-disable-next-linef
    }
    
    // Lấy cấu hình cảnh báo cháy
    const getFireConfigRoi = async () => {
        try {
            const res = await axiosClient?.get(`/aiConfigRoiList/get-list-ai-config-region-blocking/?camera_code=${cameraCode}`)
            const { polygon1, polygon2, polygon3 } = getPolygonFire(res?.region_block, width, height);
            
            setDefaultRoi(true);
            setPolygon({
                polygon1: polygon1,
                polygon2: polygon2,
                polygon3: polygon3,
            })
        } catch (error) {
            // // console.log(error);
            Toast.open(t('setting.Retrieving_fire_alarm_configuration_failed'), 'danger')
        }
    }

    useEffect(() => {
        setCanvas(canvasRef.current);
        const ctx = canvasRef.current.getContext("2d");
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        const getData = async () => {
            if (canvasRef) {
                if (nameService?.toLowerCase() === 'cảnh báo cháy') {
                    await getFireConfigRoi()
                } else {
                    await getConfigRoi();
                }
            }
        }
        getData()
        // eslint-disable-next-line
    }, [canvasRef, reGetData, cameraCode, nameService, width, height, refreshCamera])

    const drawPolygon = (ctx, lines) => {
        let xa, ya, xb, yb, x3, y3, x4, y4;
        lines.forEach((item, index) => {
            if (index === 0) {
                xa = item.x1Control;
                ya = item.y1Control;
            } else if (index === 2) {
                xb = item.x1Control;
                yb = item.y1Control;
            } else if (index === 3) {
                x3 = item.x1Control;
                y3 = item.y1Control;
            } else if (index === 4) {
                x4 = item.x1Control;
                y4 = item.y1Control;
            }
        });
        // Bắt đầu vẽ hình tứ giác
        ctx.beginPath();
        ctx.moveTo(xa, ya);
        ctx.lineTo(xb, yb);
        ctx.lineTo(x3, y3);
        ctx.lineTo(x4, y4);
        ctx.closePath();
        // Thiết lập màu và tô màu nền cho hình tứ giác
        ctx.fillStyle = nameService?.toLowerCase() === 'cảnh báo cháy' ? 'rgba(152, 1, 1, 0.04)' : 'rgba(0, 64, 255, 0.04)';
        ctx.fill();
    };

    const drawLine = (ctx, x1, y1, x2, y2, x1Control, y1Control, x2Control, y2Control, color) => {
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        // Vẽ điểm control
        const circle = new Path2D();
        circle.arc(x1Control, y1Control, 5, 0, 2 * Math.PI);
        circle.arc(x2Control, y2Control, 5, 0, 2 * Math.PI);
        ctx.fill(circle);
        ctx.fillStyle = color
    };

    const autoDraw = (lines) => {
        if (canvas) {
            const ctx = canvas.getContext("2d");
            for (let index = 0; index < 3; index++) {
                if (lines.length <= 5 && lines.length > 0) {
                    lines.forEach(({ x1, y1, x2, y2, x1Control, y1Control, x2Control, y2Control }) => {
                        drawLine(ctx, x1, y1, x2, y2, x1Control, y1Control, x2Control, y2Control, "white");
                    });
                    // Vẽ hình tứ giác khi đủ số lượng line
                    if (lines.length === 5) {
                        drawPolygon(ctx, lines);
                    }
                }
            }
        }
    }

    useEffect(() => {
        if (canvas) {
            if (cropRegion !== 0 && nameService.toLowerCase() === 'nhận dạng biển số xe') {
                const ctx = canvas.getContext("2d");
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                autoDraw(polygon.polygon1);
                autoDraw(polygon.polygon2);
                autoDraw(polygon.polygon3);
            }
            else {
                const ctx = canvas.getContext("2d");
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                // drawDefaultPolygon(defaultShape);
                autoDraw(polygon.polygon1);
                autoDraw(polygon.polygon2);
                autoDraw(polygon.polygon3);
            }
        }

    }, [canvas, cropRegion, nameService, polygon])

    useMemo(() => {
        if (restore && canvas) {
            const ctx = canvas.getContext("2d");
            ctx.clearRect(0, 0, 1000, 1000);
            setReStore(false);
            autoDraw(polygon.polygon1);
            autoDraw(polygon.polygon2);
            autoDraw(polygon.polygon3);
        }
    }, [polygon, restore, canvas, cropRegion])

    useLayoutEffect(() => {
        if (canvas) {
            const ctx = canvas.getContext("2d");
            const dpr = window.devicePixelRatio;
            canvas.width = width;
            canvas.height = height;
            // ctx.scale(dpr, dpr);
            ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
    }, [canvas, width, height]);

    // // console.log('cropRegion', cropRegion);
    return (
        <div className={cx('draw-container')}>
            <canvas
                ref={canvasRef}
                className={cx('canvas-space')}
                style={{
                    width: '100%',
                    height: `${height}px`,
                }}
            />
        </div>
    );
}

export default CanvasRegion;